import React, { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheck,
  faInbox,
  faQuestionCircle,
  faTimes,
} from '@fortawesome/pro-regular-svg-icons';
import Tippy from '@tippyjs/react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { cn } from 'src/common/utils';
import Logo from 'src/assets/UG_Logo_RGB_Lime_Blue.png';
import LogoText from 'src/assets/UG_Logo_RGB_Lime_Blue.png';
import { Tooltip } from 'src/common/components/Tooltip';
import { Paper } from 'src/common/components/Paper';
import { ActionIcon } from 'src/common/components/buttons/ActionIcon';
import {
  acceptInvitation,
  declineInvitation,
} from 'src/project/api/projectsApi';
import { Button } from 'src/common/components/buttons/Button';
import { useOnClickOutside } from 'src/common/hooks/useClickOutside';
import {
  GetProjectResponse,
  LicenseType,
  ProjectInvitation,
} from 'src/project/types/projects';
import { AppbarMobileQrCode } from './AppbarMobileQrCode';
import { MobileDocumentationNav, PrimaryNav } from './PrimaryNav';
import { MobileProjectsLink } from './MobileProjectsLink';
import { UserMenu } from './UserMenu';
import { TextLink } from 'src/common/components/TextLink';
import { isProjectOwnerOrAdmin } from 'src/user/utils';
import {
  useProjectInvitesOfCurrentUserQuery,
  useTechCrewsQuery,
} from 'src/project/queries';
import {
  hasViewAnalysisPermission,
  hasViewDocumentationPermission,
} from 'src/Conrep/App/ActionAllowed/utils';
import { useUser } from 'src/auth/user-context';
import { useCompactAppbar } from './hooks/useCompactAppbar';
import { useMediaQuery } from 'src/common/hooks/useMediaQuery';
import { getAppbarNarrowPhoneMediaQuery } from './layout';

const APPBAR_ROW_CLASSNAME = 'h-[60px] max-[480px]:h-16';

/** Temporary: help center site is unavailable. Re-enable when it is back online. */
const HELP_CENTER_APPBAR_ENABLED = false;

const LogoIcon = ({
  compact = false,
  narrow = false,
}: {
  compact?: boolean;
  narrow?: boolean;
}) => (
  <img
    src={Logo}
    className={cn(
      'shrink-0',
      narrow ? 'h-8 px-1' : compact ? 'h-10 px-2' : 'h-[60px] px-[11px]',
    )}
    alt="sitelife logo"
  />
);

const LogoFull = ({ compact = false }: { compact?: boolean }) => (
  <img
    src={LogoText}
    className={cn(compact ? 'h-10 px-2' : 'h-[60px] px-[7px]')}
    alt="sitelife logo"
  />
);

export const BaseAppbar = ({
  logo,
  leading,
  children,
  left,
}: {
  logo: React.ReactNode;
  leading?: React.ReactNode;
  left?: React.ReactNode;
  children?: React.ReactNode;
}) => {
  return (
    <header className="z-50 shrink-0 overflow-x-hidden border-b-2 border-ugLime-500 bg-white font-medium tracking-wider">
      <div className={cn('flex min-w-0 items-stretch', APPBAR_ROW_CLASSNAME)}>
        {leading ?? (
          <Link
            to="/"
            className="flex shrink-0 items-center hover:bg-white/10 focus:text-ugLime-200 focus:outline-none"
          >
            {logo}
          </Link>
        )}

        {left ? (
          <div className="flex h-full min-w-0 flex-1 overflow-x-hidden">
            {left}
          </div>
        ) : null}

        <div className="ml-auto flex shrink-0 items-stretch">{children}</div>
      </div>
    </header>
  );
};

export const SitelifeAppbar = () => {
  const isCompact = useCompactAppbar();
  const { data: invitations } = useProjectInvitesOfCurrentUserQuery();

  const hasInvitations = invitations && invitations.length > 0;

  return (
    <BaseAppbar logo={<LogoFull compact={isCompact} />}>
      {hasInvitations && (
        <ProjectInvitations compact={isCompact} invitations={invitations} />
      )}
      <AppbarMobileQrCode />
      {HELP_CENTER_APPBAR_ENABLED && !isCompact && (
        <HelpCenter compact={isCompact} />
      )}
      <UserMenu showHelpInMenu={false} />
    </BaseAppbar>
  );
};

export const ProjectAppbar = ({ project }: { project: GetProjectResponse }) => {
  const isCompact = useCompactAppbar();
  const isNarrowPhone = useMediaQuery(getAppbarNarrowPhoneMediaQuery());
  const user = useUser();
  const hasAdminPermissions = isProjectOwnerOrAdmin(user._id, project);
  const isDemoProject = project.license.type === LicenseType.DEMO;

  const { data: techCrews } = useTechCrewsQuery(project._id);
  const hasDocumentationViewingPermissions = !!techCrews?.some((tc) =>
    hasViewDocumentationPermission(user._id, tc),
  );

  const hasAnalysisViewingPermissions = !!techCrews?.some((tc) =>
    hasViewAnalysisPermission(user._id, tc),
  );

  const hasDocumentationAccess =
    hasAdminPermissions || hasDocumentationViewingPermissions;

  const { data: invitations } = useProjectInvitesOfCurrentUserQuery();

  const hasInvitations = invitations && invitations.length > 0;

  const leftNav =
    isNarrowPhone && hasDocumentationAccess ? (
      <MobileDocumentationNav />
    ) : isNarrowPhone ? null : (
      <PrimaryNav
        isDemoProject={isDemoProject}
        hasDocumentationViewingPermissions={hasDocumentationAccess}
        hasAnalysisViewingPermissions={
          hasAdminPermissions || hasAnalysisViewingPermissions
        }
      />
    );

  const mobileLeading = isNarrowPhone ? (
    <div className="flex h-full shrink-0 items-stretch">
      <Link
        to="/"
        className="flex shrink-0 items-center hover:bg-white/10 focus:text-ugLime-200 focus:outline-none"
      >
        <LogoIcon compact narrow />
      </Link>
      <MobileProjectsLink />
    </div>
  ) : undefined;

  return (
    <BaseAppbar
      logo={<LogoIcon compact={isCompact} />}
      leading={mobileLeading}
      left={leftNav}
    >
      {hasInvitations && (
        <ProjectInvitations compact={isCompact} invitations={invitations} />
      )}
      <AppbarMobileQrCode />
      {HELP_CENTER_APPBAR_ENABLED && !isCompact && (
        <HelpCenter compact={isCompact} />
      )}
      <UserMenu showHelpInMenu={false} />
    </BaseAppbar>
  );
};

const appbarButtonClasses = (compact: boolean) =>
  cn(
    'flex h-full items-center hover:bg-white/10 focus:text-blue-200 focus:outline-none',
    compact ? 'p-3' : 'p-4',
  );

const HelpCenter = ({ compact }: { compact: boolean }) => {
  const { t } = useTranslation('app');
  return (
    <Tooltip content={t('appbar.helpCenter')} position="bottom">
      <a
        href={import.meta.env.VITE_SUPPORT_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={appbarButtonClasses(compact)}
      >
        <FontAwesomeIcon
          icon={faQuestionCircle}
          size="lg"
          className="p-1.5"
          fixedWidth
        />
      </a>
    </Tooltip>
  );
};

const ProjectInvitations = ({
  invitations,
  compact,
}: {
  invitations: ProjectInvitation[] | undefined;
  compact: boolean;
}) => {
  const { t } = useTranslation(['common', 'app']);
  const queryClient = useQueryClient();

  const [isOpen, setIsOpen] = useState(false);
  const tippyEl = useRef<HTMLDivElement>(null);
  useOnClickOutside(tippyEl, () => setIsOpen(false));

  const handleAccept = async (id: string) => {
    try {
      await acceptInvitation(id);
      toast.success(t('common:status.success'));
      queryClient.invalidateQueries({
        queryKey: ['project-invitations'],
      });
      queryClient.invalidateQueries({
        queryKey: ['projects'],
      });
    } catch (e) {
      toast.error(t('common:status.failure'));
    }
  };

  const handleDecline = async (id: string) => {
    try {
      await declineInvitation(id);
      toast.success(t('common:status.success'));
      queryClient.invalidateQueries({
        queryKey: ['project-invitations'],
      });
    } catch (e) {
      toast.error(t('common:status.failure'));
    }
  };

  const hasInvitations = invitations && invitations.length > 0;
  const sortedInvitations = hasInvitations
    ? [...invitations]
        .sort(
          (a, b) =>
            new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
        )
        .reverse()
    : [];

  const filteredInvitations =
    sortedInvitations.length > 3
      ? sortedInvitations.slice(0, 3)
      : sortedInvitations;

  return (
    <Tippy
      content={
        <Paper className="w-[320px] text-shuttleGray-800" ref={tippyEl}>
          <div className="flex items-center justify-between border-b border-gray-200 p-2">
            <div className="flex-1" />
            <h3 className="font-medium">{t('app:invitations.invitations')}</h3>
            <div className="flex-1 text-right">
              <ActionIcon onClick={() => setIsOpen(false)}>
                <FontAwesomeIcon fixedWidth icon={faTimes} />
              </ActionIcon>
            </div>
          </div>
          <ul>
            {filteredInvitations.map((invite) => (
              <li key={invite._id} className="border-b-2 px-4 py-3">
                <div className="center flex items-center border-b border-gray-200 last-of-type:border-b-0">
                  <div>
                    <h5 className="break-all font-semibold">
                      {invite.projectName}
                    </h5>
                    <h5 className="break-all text-shuttleGray-600">
                      {invite.companyName}
                    </h5>
                  </div>
                  <div className="ml-auto flex pl-2">
                    <Button
                      className="h-10 w-10"
                      onClick={() => handleAccept(invite._id)}
                    >
                      <FontAwesomeIcon fixedWidth icon={faCheck} />
                    </Button>
                    <Button
                      variant="secondary"
                      className="ml-2 h-10 w-10"
                      onClick={() => handleDecline(invite._id)}
                    >
                      <FontAwesomeIcon fixedWidth icon={faTimes} />
                    </Button>
                  </div>
                </div>
              </li>
            ))}
            <li className="px-4 py-3">
              <div className="flex justify-center border-b border-gray-200 last-of-type:border-b-0">
                <TextLink to="/invitations">
                  {t('app:invitations.allInvitations')}
                </TextLink>
              </div>
            </li>
            {!hasInvitations && (
              <li className="px-4 py-3 text-center text-shuttleGray-600">
                {t('app:invitations.noPendingInvitations')}
              </li>
            )}
          </ul>
        </Paper>
      }
      interactive
      visible={isOpen}
    >
      <button
        className={cn(appbarButtonClasses(compact), 'relative')}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <FontAwesomeIcon
          icon={faInbox}
          size="lg"
          className="p-1.5"
          fixedWidth
        />
        {hasInvitations && (
          <div className="pointer-events-none absolute right-[18px] top-[18px] h-2 w-2 rounded-full bg-red-500" />
        )}
      </button>
    </Tippy>
  );
};
